import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Email as MailIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as TasksIcon,
  Contacts as ContactsIcon,
  Search as SearchIcon,
  Keyboard as ShortcutIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const helpSections = [
  {
    title: 'Getting Started',
    icon: <MailIcon sx={{ fontSize: 20 }} />,
    color: '#3b82f6',
    items: [
      { q: 'How do I compose an email?', a: 'Click the "Compose" button in the left sidebar or use the keyboard shortcut "C" to open a new email composer.' },
      { q: 'How do I read emails?', a: 'Click on any email in the inbox list to view its contents. Use the arrow keys to navigate between emails.' },
      { q: 'How do I reply or forward?', a: 'Open an email and use the Reply, Reply All, or Forward buttons at the bottom of the email view.' },
      { q: 'How do I attach files?', a: 'In the compose window, click the attachment (paperclip) icon or drag & drop files directly into the compose area.' },
    ],
  },
  {
    title: 'Organizing Email',
    icon: <FolderIcon sx={{ fontSize: 20 }} />,
    color: '#f59e0b',
    items: [
      { q: 'How do I create folders?', a: 'Click "New Folder" in the left sidebar under the folders section to create a custom folder.' },
      { q: 'How do I use labels?', a: 'Right-click an email or use the label button in the toolbar to add color-coded labels for better organization.' },
      { q: 'How do I set up filters?', a: 'Go to Settings > Filters to create rules that automatically sort, label, or forward incoming emails.' },
      { q: 'How do I search for emails?', a: 'Use the search bar at the top. You can search by sender, subject, content, date, or use advanced filters.' },
    ],
  },
  {
    title: 'Calendar',
    icon: <CalendarIcon sx={{ fontSize: 20 }} />,
    color: '#10b981',
    items: [
      { q: 'How do I create an event?', a: 'Go to Calendar from the left sidebar and click on any date/time slot to create a new event, or click the "+ New Event" button.' },
      { q: 'How do I manage calendars?', a: 'In the Calendar view, use the left panel to create new calendars, toggle visibility, or change calendar colors.' },
    ],
  },
  {
    title: 'Tasks & Notes',
    icon: <TasksIcon sx={{ fontSize: 20 }} />,
    color: '#8b5cf6',
    items: [
      { q: 'How do I create a task?', a: 'Go to Tasks from the left sidebar and click "+ New Task". You can set due dates, priorities, and assign to projects.' },
      { q: 'How do I organize notes?', a: 'Go to Notes from the left sidebar. Create folders to organize notes, use colors and tags for easy finding.' },
    ],
  },
  {
    title: 'Contacts & Bookmarks',
    icon: <ContactsIcon sx={{ fontSize: 20 }} />,
    color: '#ec4899',
    items: [
      { q: 'How do I add contacts?', a: 'Go to Contacts and click "+ New Contact". You can also add senders directly from received emails.' },
      { q: 'How do I use bookmarks?', a: 'Go to Bookmarks from the left sidebar to save and organize important web links in folders.' },
    ],
  },
  {
    title: 'Keyboard Shortcuts',
    icon: <ShortcutIcon sx={{ fontSize: 20 }} />,
    color: '#6366f1',
    items: [
      { q: 'Compose new email', a: 'Press "C" to open a new compose window.' },
      { q: 'Reply to email', a: 'Press "R" to reply to the selected email.' },
      { q: 'Forward email', a: 'Press "F" to forward the selected email.' },
      { q: 'Search emails', a: 'Press "/" to focus the search bar.' },
      { q: 'Refresh', a: 'Press "Ctrl + Shift + R" to refresh the current view.' },
      { q: 'Delete email', a: 'Press "Delete" or "#" to move the selected email to trash.' },
    ],
  },
  {
    title: 'Security & Settings',
    icon: <SecurityIcon sx={{ fontSize: 20 }} />,
    color: '#ef4444',
    items: [
      { q: 'How do I change my password?', a: 'Go to Settings > Mail Accounts to change your password.' },
      { q: 'How do I set up two-factor auth?', a: 'Go to Settings > Security to enable two-factor authentication for extra protection.' },
      { q: 'How do I manage sessions?', a: 'Go to Settings > Security to view and revoke active login sessions.' },
      { q: 'How do I set up vacation auto-reply?', a: 'Go to Settings > Out of Office to set up automatic replies when you\'re away.' },
    ],
  },
];

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);

  const filteredSections = searchQuery.trim()
    ? helpSections.map(section => ({
        ...section,
        items: section.items.filter(
          item =>
            item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.a.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(section => section.items.length > 0)
    : helpSections;

  const handleClose = () => {
    setSearchQuery('');
    setExpanded(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: isDark ? '#1e293b' : '#fff',
          border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem', color: isDark ? '#e2e8f0' : '#1e293b' }}>
            Help Center
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1, px: 2.5 }}>
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search help topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: isDark ? '#64748b' : '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: isDark ? '#0f172a' : '#f8fafc',
            },
          }}
        />

        {/* Help Sections */}
        {filteredSections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No help topics found for "{searchQuery}"
            </Typography>
          </Box>
        ) : (
          filteredSections.map((section) => (
            <Accordion
              key={section.title}
              expanded={expanded === section.title || !!searchQuery}
              onChange={(_, isExpanded) => setExpanded(isExpanded ? section.title : false)}
              sx={{
                mb: 1,
                borderRadius: '8px !important',
                border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                bgcolor: isDark ? '#0f172a' : '#fafafa',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 8px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: isDark ? '#64748b' : '#9ca3af' }} />}
                sx={{
                  minHeight: 44,
                  '& .MuiAccordionSummary-content': { my: 0.75 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '6px',
                    bgcolor: alpha(section.color, 0.15),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: section.color,
                  }}>
                    {section.icon}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {section.title}
                  </Typography>
                  <Chip label={section.items.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(section.color, 0.1), color: section.color }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
                <List dense disablePadding>
                  {section.items.map((item, idx) => (
                    <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1, px: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#cbd5e1' : '#374151', mb: 0.5 }}>
                        {item.q}
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#6b7280', lineHeight: 1.5 }}>
                        {item.a}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: `1px solid ${isDark ? '#334155' : '#e5e7eb'}` }}>
        <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#9ca3af', flexGrow: 1 }}>
          Need more help? Use the Feedback button to contact support.
        </Typography>
        <Button
          onClick={handleClose}
          sx={{ textTransform: 'none', borderRadius: '8px', color: isDark ? '#94a3b8' : '#6b7280' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog;
